import React from "react";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { FormField } from "component/shared/form-fields";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { getPayload, RootState } from "redux/store";
import { saveUser } from "redux/slice/authentication";
import { getUserUnsafe } from "redux/selector";
import { FieldHelper, FieldHelperVariant } from "../setup-dialog/field-helper";
import { styles } from "./style";
import { UserPartial } from "model/user";

const mapStateToProps = (state: RootState) => {
	const user = getUserUnsafe(state);
	return {
		user,
		loading: state.authentication.loading,
		error: state.authentication.error,
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	saveUser,
}, dispatch);

interface Props extends
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles>
{
	onSave: (areaCode: string) => void;
	label?: string;
	disabled?: boolean;
	showRequired?: boolean;
}

interface State {
	areaCode: string;
	completed: boolean;
}

const initialState: State = {
	areaCode: "",
	completed: false,
}

class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = initialState;
	}

	public componentDidMount() {
		const areaCode = this.props.user?.textMessageAreaCode;
		if (areaCode) {
			// A code saved on an earlier visit was already checked.
			this.setState({areaCode: areaCode, completed: areaCode.length === 3});
		}
	}

	private onAreaCodeChange = (value: string) => {
		if (value.length <= 3) {
			this.setState({areaCode: value}, 
				() => {
					if (this.state.areaCode.length === 3) {
						this.saveAreaCode();
					} else {
						this.setState({completed: false});
					}
				}
			);
		}
	}

	private saveAreaCode = async () => {
		const userTextMessageAreaCode = this.props.user ? this.props.user.textMessageAreaCode : undefined;
		const userPartial: UserPartial = {
			...this.props.user,
			textMessageAreaCode: this.state.areaCode,
		}
		if (this.state.areaCode !== userTextMessageAreaCode) {
			await getPayload(this.props.saveUser({user: userPartial}));
		}
		this.setState({completed: true});
		this.props.onSave(this.state.areaCode);
		
	}
	
	private helper(): { variant: FieldHelperVariant; text: string } {
		const { loading, error } = this.props;
		const { areaCode, completed } = this.state;
		if (areaCode.length === 3 && (!completed || loading)) {
			return { variant: "loading", text: "Checking for availability..." };
		}
		if (completed && error === "Area code failed") {
			return { variant: "error", text: "No numbers available for this area code. Try another." };
		}
		if (completed && !error) {
			return { variant: "success", text: "Numbers available in this area code." };
		}
		return { variant: this.props.showRequired ? "error" : "info", text: "Enter a 3-digit area code." };
	}

	public render() {
		const { disabled, label = "Area Code", classes = {} } = this.props;
		const helper = this.helper();
		return (
			<div className={classes.root}>
				<div className={classes.input}>
					<FormField
						id="texting-area-code"
						label={label}
						value={this.state.areaCode}
						type="number"
						onChange={(event) => this.onAreaCodeChange(event.target.value)}
						error={helper.variant === "error"}
						disabled={disabled}
					/>
				</div>
				<FieldHelper variant={helper.variant}>{helper.text}</FieldHelper>
			</div>
		);
	}
}

export const AreaCode = withStyles(connect(
	mapStateToProps,
	mapDispatchToProps,
)(Component), styles);


